import React, { useState } from 'react';

import { Text } from '~/components/core';
import Button from '~/components/core/Atomic/Buttons/Button';
import DndOrderedList from '~/components/core/DndOrderedList/DndOrderedList';
import type { DndOrderedListReorder } from '~/components/core/DndOrderedList/DndOrderedList.types';
import { FeedbackSnackbar } from '~/components/core/Molecules/Snackbar';
import { MainCard } from '~/components/core/NewDesignSystem/Cards/MainCard/MainCard';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import { AddIcon } from '~/components/deprecatedMuiIcons';
import type {
  ClaimStageConfigurationCreateJson,
  ClaimStageConfigurationModel,
} from '~/server_shared/openapi/data-contracts';

import ClaimStageAddEditDialog from './components/ClaimStageAddEditDialog';
import ClaimStageConfigurationEmptyState from './components/ClaimStageConfigurationEmptyState';
import StageConfigRow from './components/StageConfigRow';
import useClaimsStageConfigurationAndMutations from './hooks/useClaimStageConfigurationAndMutations';
import { STAGE_ROW_GRID } from './constants';
import TEXTS from './texts';

const ClaimStageConfigurationHeader = () => (
  <div className={`${STAGE_ROW_GRID} border-b border-slate-300 px-8 py-8`}>
    <span />
    <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
      {TEXTS.columns.color}
    </Text>
    <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
      {TEXTS.columns.key}
    </Text>
    <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
      {TEXTS.columns.label}
    </Text>
    <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY} className="justify-self-end">
      {TEXTS.columns.enabled}
    </Text>
    <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY} className="justify-self-end">
      {TEXTS.columns.actions}
    </Text>
  </div>
);

const ClaimStageConfiguration = () => {
  const [dialogState, setDialogState] = useState<{
    isOpen: boolean;
    editedStage: ClaimStageConfigurationModel | null;
  }>({ isOpen: false, editedStage: null });
  const {
    claimStageConfigurations,
    isLoading,
    isError,
    isUpdating,
    create,
    update,
    reorder,
    snackbarState,
    closeSnackbar,
  } = useClaimsStageConfigurationAndMutations();

  // Local order so a drop reorders instantly; resynced to the server whenever the fetched
  // data changes, which also reverts a failed reorder. The resync happens during render
  // (not in an effect) so the new order is reflected in the same paint — otherwise the
  // empty-state/list gate below would read a stale empty `orderedStages` for one post-load
  // paint and briefly flash "No claim stages yet" over already-configured stages.
  const [orderedStages, setOrderedStages] = useState(claimStageConfigurations);
  const [syncedConfigurations, setSyncedConfigurations] = useState(claimStageConfigurations);
  if (syncedConfigurations !== claimStageConfigurations) {
    setSyncedConfigurations(claimStageConfigurations);
    setOrderedStages(claimStageConfigurations);
  }

  const closeDialog = () => setDialogState({ isOpen: false, editedStage: null });
  const handleCreateClick = () => setDialogState({ isOpen: true, editedStage: null });
  const handleEditClick = (stage: ClaimStageConfigurationModel) => setDialogState({ isOpen: true, editedStage: stage });

  const handleSubmit = async (values: ClaimStageConfigurationCreateJson) => {
    if (dialogState.editedStage) {
      await update(dialogState.editedStage.key, values);
    } else {
      await create(values);
    }
    closeDialog();
  };

  const handleToggle = (stage: ClaimStageConfigurationModel) => update(stage.key, { is_enabled: !stage.is_enabled });

  const handleReorder = async ({ fromIndex, toIndex }: DndOrderedListReorder) => {
    const reordered = [...orderedStages];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    const { key: toStageKey } = orderedStages[toIndex];
    setOrderedStages(reordered);

    try {
      if (!(await reorder(moved.key, fromIndex, toStageKey, toIndex))) {
        // Nothing was saved: revert the optimistic move back to the server order.
        setOrderedStages(claimStageConfigurations);
      }
    } catch {
      // Unexpected rejection — revert the optimistic move rather than leak an unhandled rejection.
      setOrderedStages(claimStageConfigurations);
    }
  };

  return (
    <MainCard type="elevated" title={TEXTS.sectionTitle}>
      <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY} className="-mt-4">
        {TEXTS.sectionDescription}
      </Text>
      {isLoading || isError ? (
        <SkeletonTable rowsCount={6} columnsCount={1} isError={isError} />
      ) : orderedStages.length === 0 ? (
        <ClaimStageConfigurationEmptyState onCreateClick={handleCreateClick} />
      ) : (
        <>
          <Button color="primary" onClick={handleCreateClick} className="self-end" disabled={isUpdating}>
            <AddIcon />
            {TEXTS.addStage}
          </Button>
          <div>
            <ClaimStageConfigurationHeader />
            <DndOrderedList
              className="flex flex-col gap-8"
              disabled={isUpdating}
              items={orderedStages}
              getItemId={(stage) => stage.key}
              renderItem={(stage, { dragHandleProps }) => (
                <StageConfigRow
                  stage={stage}
                  dragHandleProps={dragHandleProps}
                  onToggle={() => handleToggle(stage)}
                  onEdit={() => handleEditClick(stage)}
                  disabled={isUpdating}
                />
              )}
              onReorder={handleReorder}
            />
          </div>
        </>
      )}

      <ClaimStageAddEditDialog
        isOpen={dialogState.isOpen}
        editedStage={dialogState.editedStage}
        onClose={closeDialog}
        onSubmit={handleSubmit}
        existingKeys={orderedStages.map((stage) => stage.key)}
      />
      <FeedbackSnackbar state={snackbarState} onClose={closeSnackbar} />
    </MainCard>
  );
};

export default ClaimStageConfiguration;
